// @ts-nocheck
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import PageBreadcrumbs, { PageBreadcrumbsStyles } from "@store/Overrides/Components/PageBreadcrumbs";
import { linkStyles, linkUnderlineStyle } from "@store/Themes/common/mixins/link";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { siteWidthStyles } from "@toolkit/Themes/common/mixins";
import parse from "html-react-parser";
import React, { useContext } from "react";
import { css } from "styled-components";

const CategoryName = getWidgetComponent("Category/CategoryName");

const enum fields {
    hideBreadcrumbs = "hideBreadcrumbs",
}

interface Props extends WidgetProps {
    fields: {
        [fields.hideBreadcrumbs]: boolean;
    };
}

export interface CategoryDetailsHeaderStyles {
    wrapper?: InjectableCss;
    overlayWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    textWrapper?: InjectableCss;
    breadcrumbStyles?: PageBreadcrumbsStyles;
    informationWrapper?: InjectableCss;
}

const contentMaxWidth = pxToRem(560);

export const categoryDetailsHeaderStyles: CategoryDetailsHeaderStyles = {
    wrapper: {
        css: css`
            position: relative;
            height: 19.875vh;
            background-color: ${getColor("primary")};
            border-radius: ${({ theme }) => theme.design.layout.bannerBorderRadiusMobile};
            color: ${getColor("primary.contrast")};
            transition: ${transitionHoverStyles("border-radius")};

            ${breakpoint("md")} {
                background: ${getColor("common.gradient1")};
            }

            ${breakpoint("md")} {
                border-radius: ${({ theme }) => theme.design.layout.bannerBorderRadius};
            }
        `,
    },
    overlayWrapper: {
        css: css`
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            display: flex;
            align-items: center;
        `,
    },
    titleWrapper: {
        css: css`
            width: 100%;
            max-width: ${contentMaxWidth};
        `,
    },
    contentWrapper: {
        css: css`
            max-width: ${contentMaxWidth};
            margin-top: ${pxToRem(15)};

            body {
                background-color: transparent;
                color: ${getColor("primary.contrast")};
            }
        `,
    },
    breadcrumbStyles: {
        breadcrumbs: {
            css: css`
                li {
                    color: ${getColor("primary.contrast")};
                }

                a {
                    ${linkStyles("inherit", "primary.contrast", false, false)}
                    ${linkUnderlineStyle("primary.contrast", "primary.contrast")}

                    ${({ theme }) => `
                        font-weight: ${theme.design.typography.weight.base};
                    `}
                }

                ${breakpoint("sm", "down")} {
                    display: none;
                }
            `,
            mergeCss: true,
        },
    },
    informationWrapper: {
        css: css`
            ${siteWidthStyles()}
            height: calc(100% - ${pxToRem(40)}); /* height of the breadbrumb */
            display: flex;
            flex-direction: column;
            justify-content: center;

            ${breakpoint("sm", "down")} {
                height: 100%;
            }
        `,
    },
};

const styles = categoryDetailsHeaderStyles;

const CategoryDetailsHeader: React.FC<Props> = ({ fields }) => {
    const category = useContext(CategoryContext);

    if (!category) {
        return null;
    }

    const wrapperStyles: InjectableCss = {
        ...styles.wrapper,
        css: css`
            ${styles.wrapper?.css}

            ${category.htmlContent
                ? css`
                      height: 26.5vh;
                  `
                : css`
                      height: 19.875vh;
                  `}
        `,
    };

    return (
        <StyledWrapper {...wrapperStyles}>
            {!fields.hideBreadcrumbs ? <PageBreadcrumbs overridedStyles={styles.breadcrumbStyles} /> : null}
            <StyledWrapper {...styles.informationWrapper}>
                <StyledWrapper {...styles.titleWrapper}>
                    <CategoryName />
                </StyledWrapper>
                {category.htmlContent ? (
                    <StyledWrapper {...styles.contentWrapper}>
                        <StyledWrapper>{parse(category.htmlContent, parserOptions)}</StyledWrapper>
                    </StyledWrapper>
                ) : null}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: CategoryDetailsHeader,
    definition: {
        group: "Categories",
        displayName: "Category Details Header",
        fieldDefinitions: [
            {
                name: fields.hideBreadcrumbs,
                displayName: "Hide categories breadcrumbs",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
